import { jsPDF } from "jspdf";

/**
 * Un document du dossier, en PDF.
 *
 * RETOUR DU 08/09 : « si tu peux ajouter Telecharger en PDF disponible pour
 * chaque document ce serait parfait car le SIL ne semble accepter que les
 * PDF. »
 *
 * Les documents sont photographies par la tablette et vivent en JPEG dans le
 * Storage. Le SIL, lui, n'avale que du PDF. Personne n'a envie de faire la
 * conversion a la main, dossier par dossier, devant un patient qui attend.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POURQUOI DANS LE NAVIGATEUR, ET PAS SUR LE SERVEUR
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Le backend sait deja fabriquer des PDF (`pdfkit`, controleur `pdf`), et
 * c'etait le premier reflexe. Mais le back-office parle au service Cloud Run
 * `prod`, qui est **partage entre la preprod et la production** : y ajouter
 * une route, c'est deployer en production pour livrer une preprod. Le
 * navigateur a l'image sous la main, il la convertit lui-meme, et personne ne
 * touche a un service partage pour une demande de confort.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CE QUE CETTE CONVERSION PRESERVE, ET CE QU'ELLE COUTE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * L'image est REDESSINEE (canvas) puis reencodee en JPEG avant d'etre posee
 * dans le PDF. Cela coute une generation de compression — invisible a l'oeil
 * a 92 %, mesurable sur un histogramme. En echange, ce chemin accepte
 * n'importe quel format d'entree, y compris ceux qu'on n'a pas prevus (PNG,
 * HEIC decode par le navigateur), sans code particulier pour chacun.
 *
 * L'image est posee ENTIERE, jamais rognee, centree sur une page a son
 * orientation. Un rognage, sur une ordonnance, peut emporter une ligne de
 * prescription : c'est la seule regle qui ne se negocie pas ici.
 *
 * UN PDF RESTE UN PDF. Si le document est deja au format PDF, il est rendu
 * tel quel, sans passer par l'image — le reencoder ne ferait que degrader du
 * texte net en photographie de texte.
 */

/** Au-dela, on reduit : un canvas geant echoue en silence sur certains postes. */
const COTE_MAX = 2400;

/** 92 % : la limite au-dessous de laquelle les fins traits d'un stylo bavent. */
const QUALITE_JPEG = 0.92;

/** Les quatre premiers octets d'un PDF, qui ne mentent pas sur son type. */
async function estDejaUnPdf(blob) {
  if (blob.type === "application/pdf") return true;
  try {
    const entete = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
    return String.fromCharCode(...entete) === "%PDF-";
  } catch {
    return false;
  }
}

/** Charge un blob image et rend l'element, une fois ses dimensions connues. */
function chargerImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Ce document n'a pas pu être lu comme une image."));
    };
    img.src = url;
  });
}

/**
 * Le blob PDF d'un document.
 *
 * Rejette plutot que de rendre un PDF vide : un fichier de zero page envoye
 * au SIL est pire qu'un echec visible, il se decouvre trois jours plus tard.
 */
export async function documentEnPdf(blob) {
  if (!blob || !blob.size) {
    throw new Error("Document vide : rien à convertir.");
  }
  if (await estDejaUnPdf(blob)) return blob;

  const img = await chargerImage(blob);
  const largeurSource = img.naturalWidth || img.width;
  const hauteurSource = img.naturalHeight || img.height;
  if (!largeurSource || !hauteurSource) {
    throw new Error("Ce document n'a pas de dimensions lisibles.");
  }

  const reduction = Math.min(1, COTE_MAX / Math.max(largeurSource, hauteurSource));
  const largeur = Math.round(largeurSource * reduction);
  const hauteur = Math.round(hauteurSource * reduction);

  const canvas = document.createElement("canvas");
  canvas.width = largeur;
  canvas.height = hauteur;
  const ctx = canvas.getContext("2d");
  // Un JPEG n'a pas de transparence : sans ce fond, un PNG transparent
  // ressortirait sur du noir.
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, largeur, hauteur);
  ctx.drawImage(img, 0, 0, largeur, hauteur);

  const jpeg = canvas.toDataURL("image/jpeg", QUALITE_JPEG);

  // La page prend l'orientation du document : une ordonnance a l'italienne
  // sur une page a la francaise se retrouverait deux fois plus petite.
  const paysage = largeur > hauteur;
  const pdf = new jsPDF({
    orientation: paysage ? "landscape" : "portrait",
    unit: "pt",
    format: "a4",
  });

  const margeExterieure = 18;
  const largeurPage = pdf.internal.pageSize.getWidth();
  const hauteurPage = pdf.internal.pageSize.getHeight();
  const dispoL = largeurPage - margeExterieure * 2;
  const dispoH = hauteurPage - margeExterieure * 2;

  // On tient dans la page par le cote le plus contraignant : l'image entiere,
  // toujours, jamais rognee.
  const echelle = Math.min(dispoL / largeur, dispoH / hauteur);
  const l = largeur * echelle;
  const h = hauteur * echelle;

  pdf.addImage(
    jpeg,
    "JPEG",
    (largeurPage - l) / 2,
    (hauteurPage - h) / 2,
    l,
    h,
    undefined,
    "FAST"
  );

  return pdf.output("blob");
}

export default documentEnPdf;
